/**
 * 전자계약의 **값 집합** — 잎 노드다(`payroll-values.ts`·`attendance-values.ts` 와 같은 자리).
 *
 * 아무것도 import 하지 않는다. `api.ts` 에 두면 그것을 쓰는 파일과 `api.ts` 사이에
 * 순환이 생긴다(2026-09-05 에 한 번 밟았고 `pnpm boundaries` 가 막았다).
 */

/**
 * 계약 문서의 상태. **닫힌 집합이고 contracts 가 소유한다** — 전이 규칙은
 * `packages/core` 의 순수 함수 하나(`nextContractStatus`)가 갖고, DB check 는 이 집합의
 * **사본 하나**다(AGENTS.md 26).
 *
 * ─── 여섯인 이유, 그리고 구 시스템이 넷이라고 적은 이유 ───
 * 구 `DATABASE_SCHEMA.md` 는 넷으로 적었지만 실제 PG enum 은 여섯이다 —
 * 마이그레이션 `20260519000001` 이 `DRAFT`·`CANCELLED` 를 나중에 붙였고 그 주석이
 * "기존 버그"라고 자백한다. 문서 ≠ 코드 ≠ 데이터일 때 **데이터가 진실이다**(계획서 §5-c F).
 *
 * ─── 넷은 진행이고 둘은 끝이다 ───
 *   draft     아직 안 보냈다. 우리 안의 사실뿐이고 상대는 이 문서를 모른다.
 *   sent      서명을 요청했다. 상대가 열 수 있다.
 *   signing   일부가 서명했다(순차 서명이면 다음 사람을 기다린다).
 *   completed 전원이 서명했다. **끝이다.**
 *   expired   유효기간이 지났다. **끝이다.**
 *   cancelled 사람이 되돌렸다. **끝이다.**
 *
 * 구 `PENDING` 을 `sent` 로 바꿨다. `pending` 은 이 리포에서 이미 다른 뜻으로 쓰이고
 * (휴가 신청의 '결재 대기' — 우리가 답할 차례다), 계약의 그 상태는 **상대가 답할 차례**라
 * 방향이 반대다. 같은 낱말이 두 방향을 가리키면 화면 문구가 둘 다 "대기 중"이 된다.
 *
 * 값이 소문자인 이유는 이 리포의 다른 상태 문자열이 전부 소문자이기 때문이다
 * (`SALARY_TYPES`·`ATTENDANCE_STATUSES` 와 같은 판단). 구 시스템은 PG enum 에 대문자였다.
 */
export const CONTRACT_STATUSES = [
  'draft', 'sent', 'signing', 'completed', 'expired', 'cancelled',
] as const;
export type ContractStatus = (typeof CONTRACT_STATUSES)[number];

/**
 * 끝난 상태 셋. **여기서는 아무 데로도 못 간다** — 되돌리는 문이 없다.
 *
 * 되돌리기를 안 만든 이유: 서명은 상대의 행위이고 우리가 취소할 수 있는 것은 **요청**뿐이다.
 * 완료를 초안으로 되돌리면 "서명된 문서가 있는 초안"이 표현 가능해진다. 잘못 만든 계약은
 * `cancelled` 로 닫고 새로 만든다 — 0031 의 확정 급여와 같은 판단이고, 다른 점은 여기에
 * `rollback` 이 아예 없다는 것이다(0033 의 퇴직정산은 금액이 계산 결과라 되돌릴 수 있었다).
 */
export const CONTRACT_TERMINAL_STATUSES = ['completed', 'expired', 'cancelled'] as const;

/**
 * 계약 유형. 구 PG enum 과 같은 둘이다(`FREELANCER`·`LABORER`).
 *
 * `laborer` 는 근로계약(근로기준법), `freelancer` 는 도급·위임이다. 그 둘은 원천징수도
 * 4대보험도 다르므로 **문서 종류가 아니라 관계의 종류**이고, 나중에 급여 쪽이 이 값을
 * 물을 수 있다(구 `contract_templates.default_contract_type` 이 그 자리였다).
 */
export const CONTRACT_TYPES = ['laborer', 'freelancer'] as const;
export type ContractType = (typeof CONTRACT_TYPES)[number];

/**
 * ★ **종료일을 유형으로 강제하지 않는다** — 구 시스템의 check 를 그대로 옮기지 않은
 * 유일한 자리다.
 *
 * 구 `valid_end_date` 는 이렇게 적혀 있다:
 *   `LABORER AND end_date IS NULL` **OR** `FREELANCER AND end_date IS NOT NULL`
 * 즉 근로계약에는 종료일이 있으면 **INSERT 가 실패한다**. 그런데 기간제 근로계약은
 * 기간제법이 정한 실재하는 계약이고(2년 상한이 그 법의 조문이다), 그걸 표현할 수 없는
 * 시스템은 계약직을 쓰는 회사를 받을 수 없다. 실측을 사지 않은 이유가 이것이다 —
 * 이 답은 구 데이터가 아니라 법에서 온다(AGENTS.md §정본 판정표 3행).
 *
 * 남는 불변식은 하나뿐이고 유형과 무관하다: **종료일이 있으면 시작일보다 뒤다.**
 */
/**
 * 계약 유형의 라벨 키. **`Record<Union, string>` 이라 값이 하나 늘면 컴파일이 죽는다**
 * (`contract-labels.ts` 의 그 판단 — 파생 키를 만들면 i18n 게이트가 영영 못 본다).
 *
 * ★ 이 표가 화면이 아니라 `packages/contracts` 에 사는 이유(0042): **서버도 이것을 쓴다.**
 * 계약서 필드를 채울 때 `laborer` 를 그대로 찍을 수는 없어서 서버가 번역된 라벨을
 * 넣는데, `apps/server` 는 `packages/core-web` 을 import 할 수 없다(경계 룰). 양쪽이
 * 각자 들면 화면의 "근로계약"과 서류의 "근로 계약"이 갈리는 날이 온다.
 */
export const CONTRACT_TYPE_LABEL: Record<ContractType, string> = {
  laborer: 'esign.screen.contract.type.laborer',
  freelancer: 'esign.screen.contract.type.freelancer',
};

export const CONTRACT_TEXT_MAX = 200;

/* ═══════════════════════════════════════════════════════════════════════════
 * 서명 채널 — **여기서부터는 우리 사실이 아니라 밖의 사실이다.** (docs/decisions/0042)
 * ═══════════════════════════════════════════════════════════════════════════ */

/**
 * 서명 채널이 문서에 붙이는 상태 다섯 (유캔사인, 2026-09-09 구 리포 실측).
 *
 * 구 시스템에는 이 목록이 **어디에도 없었다** — `d['status'] == 'completed'` 같은 문자열
 * 비교가 잡·서비스·화면에 흩어져 있었고, 사람이 읽는 라벨만 `frontend/src/utils/ucansign.ts`
 * 에 다섯 줄 있었다. 그래서 `on_going` 이 우리 상태 어디로 가는지 아무도 한 곳에서 말할 수
 * 없었다(그리고 실제로 웹훅과 잡이 서로 다르게 판정했다 — 웹훅은 `SIGNING`, 잡은 안 건드림).
 *
 *   need_signing       상대가 서명해야 한다(순차 서명에서 지금 차례)
 *   wait_for_signing   앞사람을 기다린다
 *   on_going           일부가 서명했다
 *   completed          전원이 서명했다
 *   request_cancelled  요청이 취소됐다
 *
 * **만료는 상태가 아니라 문서의 플래그다**(`expired: true`) — 그래서 여섯이 아니라 다섯이다.
 */
export const ESIGN_PROVIDER_STATUSES = [
  'need_signing', 'wait_for_signing', 'on_going', 'completed', 'request_cancelled',
] as const;
export type ESignProviderStatus = (typeof ESIGN_PROVIDER_STATUSES)[number];

/**
 * 웹훅 이벤트 넷 (같은 실측).
 *
 * ★ **이 값들은 무엇을 할지 정하지 않는다 — "다시 읽어라"는 신호일 뿐이다**(0042).
 * 구 시스템은 이벤트 이름으로 상태를 정했다: `signing_completed_all` 이 오면 그대로
 * `COMPLETED` + `signed_at` 을 썼다. 그 엔드포인트에는 **인증도 서명 검증도 없었으므로**
 * 인터넷의 아무나 남의 계약을 완료로 만들 수 있었다. 여기서는 이벤트가 오면 우리가
 * API 로 문서를 **다시 읽어** 상태를 정한다 — 그래서 이 유니온은 로그와 멱등 키에만 쓴다.
 */
export const ESIGN_WEBHOOK_EVENTS = [
  'sign_creating', 'signing_completed', 'signing_completed_all', 'signing_canceled',
] as const;
export type ESignWebhookEvent = (typeof ESIGN_WEBHOOK_EVENTS)[number];

/** 웹훅 이벤트 행의 처리 상태. `received` 는 "받았고 아직 안 끝났다"이다. */
export const ESIGN_WEBHOOK_STATUSES = ['received', 'processed', 'failed', 'ignored'] as const;
export type ESignWebhookStatus = (typeof ESIGN_WEBHOOK_STATUSES)[number];

/**
 * 템플릿 필드의 종류. 유캔사인이 주는 타입 중 **우리가 값을 채울 수 있는 것**만이다.
 *
 * 서명·도장 필드는 여기 없다 — 그건 사람이 그 자리에서 하는 것이고, 값을 미리 넣는다는
 * 개념이 없다. 구 시스템도 매핑 대상에서 `signature` 를 빼고 있었다(쿼리의 `where.not`).
 */
export const ESIGN_FIELD_TYPES = ['text', 'date', 'number', 'checkbox'] as const;
export type ESignFieldType = (typeof ESIGN_FIELD_TYPES)[number];

/**
 * 필드를 채우는 **값의 출처.** 닫힌 집합이고 DB check 가 사본 하나다(AGENTS.md 26).
 *
 * ─── ★ 방향이 하나다: 우리 값이 문서로 간다 ───
 * 구 카탈로그(`ContractColumnCatalog`)는 그룹이 셋이고 **방향이 둘**이었다:
 * `employees.*`·`settings.*` 는 채우기였고 `contracts.*` 는 **되돌려 쓰기**였다 —
 * 서명이 끝나면 문서의 필드값을 계약 행의 컬럼에 썼다(`Contracts::SyncService`).
 * 그 목록에 `status`·`signed_at` 이 들어 있었다. 즉 **매핑 화면에서 PDF 의 텍스트 칸을
 * 계약 상태에 연결할 수 있었다** — 상태 집합의 소유자가 하나여야 한다는 규칙(AGENTS.md 26)이
 * 정확히 막는 형태이고, 그 경로로 들어온 값에는 전이 검사도 없었다.
 *
 * 여기서는 되돌려 쓰기가 **없다.** 문서에서 우리 표로 오는 사실은 셋뿐이고(상태 · 서명일 ·
 * 서명본 파일) 그 셋은 매핑이 아니라 **채널의 사실**이라 사람이 연결하는 것이 아니다.
 * 그래서 잡 하나(`sync_contract_job`)와 계약 행의 `ucansign_field_bindings` 컬럼이 함께 사라졌다.
 */
export const ESIGN_FIELD_SOURCES = [
  // 계약 행 자신 — 우리가 만들 때 정한 값이다.
  'contract.title', 'contract.type', 'contract.startDate', 'contract.endDate',
  // 계약 상대.
  'employee.name', 'employee.employeeNo', 'employee.email', 'employee.phone',
  'employee.joinedOn',
  /*
   * ★ 등록 급여 — **그 계약의 시작일에 유효한 계약**이다(`effectiveContractAt`, 0032).
   * `employees` 에서 읽지 않는다: `base_salary` 는 0027 이 드롭했고 원본은 이력 표다.
   * "이 날 유효한 계약"의 소유자가 하나라는 것이 AGENTS.md 46 이고, 여기가 그 소비자다.
   */
  'employee.salaryType', 'employee.wage',
  /*
   * 0051 에서 열렸다. 아래 ★★ 주석이 "칸이 아예 없어서 못 넣는다"고 적어 둔 자리이고,
   * 그 칸이 생기면서 **주민등록번호를 푸는 유일한 소비자**가 됐다 —
   * 화면으로 나가는 경로는 여전히 0개다(목록은 마스킹만 본다).
   */
  'employee.residentNumber', 'employee.birthDate',
  // 회사 — `core.company_settings`(0039) + 최저시급.
  'company.legalName', 'company.businessNumber', 'company.representativeName',
  'company.address', 'company.phone', 'company.email', 'company.minimumHourlyWage',
] as const;
export type ESignFieldSource = (typeof ESIGN_FIELD_SOURCES)[number];

/*
 * ★★ **구 카탈로그에 있었는데 여기 없는 것 셋 — 그리고 그것이 결핍이 아닌 이유.**
 *
 *   ~~`employees.resident_id`(주민등록번호) · `birth_date`~~ → **0051 에서 열렸다.**
 *     여기 적혀 있던 것은 "저장하는 것 자체가 결정이다(저장 위치·암호화·누가 보나·파기)"
 *     이고 그 순서는 지켰다 — 결정이 먼저 났고(`core.employee_identities`: 별도 표 ·
 *     AES-256-GCM · 권한 하나 · 마스킹만 나감) 그다음에 이 두 줄이 열렸다.
 *     ★ **채우기가 그 값을 푸는 유일한 자리다** — 화면으로 나가는 경로는 없다.
 *   `employees.address`·`postal_code`
 *     → 여전히 칸이 없다. 주소는 계약서에 흔히 들어가지만 이 리포가 아직 안 만들었고,
 *       만드는 것은 명부의 결정이지 템플릿 필드의 결정이 아니다(위와 같은 순서).
 *   `employees.bank_code`·`bank_account`
 *     → 표가 다르고 권한이 다르다(`core.employee_bank_accounts` — AGENTS.md 52).
 *       서명을 요청하는 사람이 계좌 권한을 가졌다는 보장이 없는데, 채우기는 **서버가
 *       그 사람 대신** 하는 일이다. 즉 매핑 한 줄이 권한 검사를 우회하는 문이 된다.
 *       계좌가 든 서류(급여계좌 등록)가 실제로 필요해지면 그때 **그 권한과 함께** 온다.
 *   `settings.image_payment_rate`·`video_payment_rate`
 *     → FFL vertical 의 값이다(0039 실측). 코어가 그 이름을 알면 AGENTS.md 1 위반이고,
 *       필요해지면 `packages/contracts` 의 확장점으로 온다.
 */

export const ESIGN_FIELD_SOURCE_GROUPS = ['contract', 'employee', 'company'] as const;
export type ESignFieldSourceGroup = (typeof ESIGN_FIELD_SOURCE_GROUPS)[number];

/** `employee.residentId` 처럼 `<그룹>.<이름>` 이다. 화면이 문자열을 자르지 않게 함수가 답한다. */
export const esignFieldSourceGroupOf = (source: ESignFieldSource): ESignFieldSourceGroup =>
  source.slice(0, source.indexOf('.')) as ESignFieldSourceGroup;

/**
 * 채우면 **급여가 문서로 나가는** 출처. 매핑 화면이 그 사실을 경고로 말한다.
 *
 * 목록으로 두는 이유: "민감한가"는 출처마다 다른 사실이고, 화면이 문자열로 판정하면
 * (`source.includes('resident')`) 다음에 계좌가 추가될 때 조용히 빠진다.
 */
export const ESIGN_SENSITIVE_FIELD_SOURCES: readonly ESignFieldSource[] = [
  'employee.wage', 'employee.salaryType',
  /*
   * 0051. 급여보다 **더** 민감하다 — 매핑 화면이 경고로 말하는 것이 여기서는
   * "이 값이 종이로 나간다"이고, 그 종이는 회수할 방법이 없다(0044 가 첨부 대신 만료
   * 링크를 고른 그 사고와 같은 자리).
   */
  'employee.residentNumber',
];

/** 템플릿 이름·필드 이름의 상한. 계약명(`CONTRACT_TEXT_MAX`)과 같은 자리에서 온다. */
export const ESIGN_TEMPLATE_TEXT_MAX = 200;

/**
 * 자격증명의 **이름**(값이 아니다). 서버 설정에서 이 이름으로 키를 찾는다 —
 * 그래서 DB 에도 응답에도 키가 없다(전역 CLAUDE.md §18 · §27).
 */
export const ESIGN_CREDENTIAL_REF_RE = /^[a-z0-9][a-z0-9-]{0,30}[a-z0-9]$/;

/** 웹훅 URL 의 프리픽스. `FILE_PREFIX` 와 같은 논거로 contracts 가 소유한다(AGENTS.md 20). */
export const ESIGN_WEBHOOK_PREFIX = '/esign/webhook';

/**
 * 웹훅 경로. **키가 곧 자격증명이다** — URL 을 아는 것이 이 문을 여는 유일한 방법이고,
 * DB 에는 sha256 만 남는다(세션·업로드 티켓과 같은 판단 — 0023·0030).
 */
export const esignWebhookPathOf = (key: string): string => `${ESIGN_WEBHOOK_PREFIX}/${key}`;

/* ═══════════════════════════════════════════════════════════════════════════
 * 직접 서명 — **채널 없이 우리가 서명받는다.** (docs/decisions/0221)
 *
 * 위의 채널(유캔싸인)은 그대로 있다. 이 아래는 그 옆의 두 번째 길이고, 사실의 주인이
 * 반대다: 채널의 문서는 채널이 갖고 우리는 다시 묻지만(0042 · 규칙 58), 직접 서명의
 * 문서·서명·증거는 **전부 우리 표에 있다** — 물을 곳이 없다.
 * ═══════════════════════════════════════════════════════════════════════════ */

/**
 * 양식 본문의 자리표시. `{{employee.name}}` 처럼 쓰고 둘 중 하나다:
 *   - 채우기 출처(`ESIGN_DIRECT_SOURCES`) — 서버가 그 계약의 값으로 채운다
 *   - `input.<키>` — 서명을 요청하는 사람이 그때 적는다(근무장소 · 근로시간처럼 계약마다 다른 것)
 *
 * 사람이 손으로 치는 문법이 아니다 — 편집 화면이 버튼으로 끼운다. 그래도 문자열에 사는
 * 이유는 양식이 **문서**이기 때문이다: 조항 안의 어느 자리에 값이 들어가는지가 곧 내용이다.
 */
export const ESIGN_FORM_TOKEN_RE = /\{\{\s*([A-Za-z0-9_.]+)\s*\}\}/g;

/** 입력칸의 키. 자리표시 `{{input.<키>}}` 안에 들어가므로 짧고 단순하다. */
export const ESIGN_FORM_INPUT_KEY_RE = /^[a-z][a-zA-Z0-9]{0,23}$/;

/** `{{input.<키>}}` 의 앞부분. 화면과 서버가 같은 문자열을 만든다. */
export const ESIGN_FORM_INPUT_PREFIX = 'input.';

/**
 * ★ **직접 서명 양식에 못 넣는 출처 — 주민등록번호.**
 *
 * 채널 서식에서는 넣을 수 있다(0051): 값이 채널로 나가고 우리 표에는 안 남는다. 직접 서명은
 * 반대로 **확정된 문서 내용을 우리 표에 그대로 얼린다**(`esign_signings.content`) — 서명이
 * 무엇에 대한 것이었는지가 증거이기 때문이다. 그 내용에 번호가 들어가면 암호문으로만 두기로 한
 * 값(규칙 52 · 0051)의 **평문 사본**이 생긴다. 근로계약서에 주민등록번호는 법정 기재사항이
 * 아니다(근로기준법 17조가 정하는 것은 임금·근로시간·휴일·휴가다).
 */
export const ESIGN_DIRECT_EXCLUDED_SOURCES: readonly ESignFieldSource[] = ['employee.residentNumber'];

/** 직접 서명 양식이 쓸 수 있는 출처. 위 하나를 뺀 전부다 — 목록을 다시 적지 않는다. */
export const ESIGN_DIRECT_SOURCES: readonly ESignFieldSource[] = ESIGN_FIELD_SOURCES.filter(
  (s) => !ESIGN_DIRECT_EXCLUDED_SOURCES.includes(s),
);

/**
 * 양식의 상한. 근로계약서 한 장이 조항 열 개 안팎이고(표준근로계약서가 11개), 넉넉히 잡아도
 * 화면 하나·PDF 몇 장에 들어가야 한다. 상한이 없으면 jsonb 한 칸이 무한히 큰다.
 */
export const ESIGN_FORM_LIMITS = {
  name: 200,
  documentTitle: 100,
  clauses: 40,
  clauseTitle: 100,
  clauseBody: 2000,
  inputs: 30,
  inputLabel: 50,
  inputValue: 500,
} as const;

/**
 * 손으로 그린 서명 — **획의 좌표다(그림 파일이 아니다).**
 *
 * 그림(PNG)으로 받지 않는 이유: 앱(react-native-svg)은 그림을 뽑을 수 없고, 웹과 앱이 다른
 * 형식을 보내면 서버가 둘을 다 읽어야 한다. 좌표면 같은 값이 화면(SVG)과 PDF(벡터 경로)에
 * 그대로 그려지고, 크기가 그림보다 훨씬 작다.
 *
 * `strokes` 의 한 원소가 손가락을 한 번 대고 뗀 획이고, `[x0, y0, x1, y1, …]` 로 납작하다.
 * 좌표는 서명 칸(`width × height`) 안의 값이다.
 */
export interface SignatureStrokes {
  readonly width: number;
  readonly height: number;
  readonly strokes: readonly (readonly number[])[];
}

/**
 * 서명 획의 상한과 하한. 하한이 요점이다 — **점 하나는 서명이 아니다.** 한 번 톡 친 것을
 * 받으면 "서명했다"가 사람의 의사 없이 표현 가능해진다. 칸의 일정 비율 이상을 지나가야 한다.
 */
export const SIGNATURE_LIMITS = {
  maxStrokes: 80,
  maxPoints: 6000,
  /** 획 전체가 차지하는 폭(또는 높이)이 칸의 이 비율보다 작으면 서명이 아니다. */
  minExtentRatio: 0.15,
  maxBoxSide: 4000,
} as const;
